import { makeStyles } from '@mui/styles';
import { MasonTheme } from '@bymason-private/controller-v1-theme-augmentation';
import { Divider } from '../../mui/Divider';
import { Link } from '../../mui/Link';
import { Menu } from '../../mui/Menu';
import { ControllerMenuItem } from '../ControllerMenuItem';
import { Text } from '../Text';

export interface ControllerSectionedMenuSectionItem<A> {
  href?: string;
  id: A;
  icon?: JSX.Element;
  text: string;
  onClick?: () => void;
  selected?: boolean;
}

export interface SectionedMenuSection<A> {
  caption: string;
  hideCaption?: boolean;
  items: ControllerSectionedMenuSectionItem<A>[];
}

export interface ControllerSectionedMenuProps<A> {
  anchorElem: HTMLDivElement | null;
  open: boolean;
  sections: SectionedMenuSection<A>[];
  onItemClick?: (itemId: A) => void;
  onClose: () => void;
}

const useStyles = makeStyles((theme: MasonTheme) => ({
  menuItemIconRoot: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    marginRight: theme.spacing(1),
  },
  dividerRoot: {
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
  },
  sectionCaption: {
    ...theme.typography.caption,
    marginLeft: theme.spacing(1),
  },
}));

export function ControllerSectionedMenu<A = string>({
  anchorElem,
  onClose,
  onItemClick,
  open,
  sections,
}: ControllerSectionedMenuProps<A>) {
  const classes = useStyles();

  return (
    <Menu
      anchorEl={anchorElem}
      anchorOrigin={{ horizontal: 'left', vertical: 'top' }}
      open={open}
      onClose={onClose}
      transformOrigin={{ horizontal: 'left', vertical: 'bottom' }}
    >
      {sections.reduce<JSX.Element[]>((tally, section, index) => {
        if (!section.hideCaption) {
          tally.push(
            <Text className={classes.sectionCaption} key={`${section.caption}-${index}`}>
              {section.caption}
            </Text>
          );
        }

        section.items.forEach((item) => {
          tally.push(
            <ControllerMenuItem
              key={item.text}
              onClick={() => {
                if (item.onClick) {
                  item.onClick();
                }

                if (onItemClick) {
                  onItemClick(item.id);
                }
              }}
              selected={item.selected}
            >
              {item.icon && <div className={classes.menuItemIconRoot}>{item.icon}</div>}
              {item.href ? (
                <Link
                  href={item.href}
                  rel='noopener noreferrer'
                  tabIndex={-1}
                  target='_blank'
                  underline='none'
                >
                  {item.text}
                </Link>
              ) : (
                item.text
              )}
            </ControllerMenuItem>
          );
        });

        if (index < sections.length - 1) {
          tally.push(
            <Divider classes={{ root: classes.dividerRoot }} key={`${section.caption}-divider-${index}`} />
          );
        }

        return tally;
      }, [])}
    </Menu>
  );
}
